import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { AdCard } from "@/components/community/AdCard";
import { SectionHead } from "@/components/shop/SectionHead";
import { SELLER_NOTE } from "@/data/community";
import { BeeBadge } from "@/components/community/BeeBadge";

const shuffle = (xs) => xs.map((x) => [Math.random(), x]).sort((a, b) => a[0] - b[0]).map(([, x]) => x);
// Up to 8 cards: every paid Homepage feature first in a fresh random order on each visit (fair exposure), then the newest live ads.
const pick = (ads) => [...shuffle(ads.filter((a) => a.featured)), ...ads.filter((a) => !a.featured).sort((a, b) => (b.approved_at || "").localeCompare(a.approved_at || ""))].slice(0, 8);

// Homepage window into The B Marketplace.
export const CommunityFeatured = () => {
  const [ads, setAds] = useState(null);
  useEffect(() => { api.get("/community/ads").then((r) => setAds(pick(r.data))).catch(() => setAds([])); }, []);
  const shown = ads || [];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="community-featured">
      <div className="rounded-3xl border-2 border-dashed border-[#202A35]/20 bg-white p-5 sm:p-7">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-start gap-3">
            <BeeBadge size={48} className="mt-1" testId="community-featured-badge" />
            <SectionHead eyebrow="From sellers across the UK" title="The B Marketplace" text="Buy and sell across the UK: items from independent private sellers and businesses." />
          </div>
          <div className="flex flex-wrap gap-2 -mt-5 sm:mt-0 mb-6">
            <Link to="/community" className="h-10 px-4 rounded-full border-2 border-[#202A35] text-sm font-bold inline-flex items-center hover:bg-[#FFF9E8] transition-colors" data-testid="community-featured-see-all">See all ads on The B Marketplace →</Link>
            <Link to="/community/new" className="h-10 px-4 rounded-full bg-[#202A35] text-white text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#111821] transition-colors" data-testid="community-featured-post-ad"><Plus size={15} /> Post a free ad</Link>
          </div>
        </div>
        {shown.length ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="community-featured-grid">{shown.map((a) => <AdCard key={a.id} ad={a} />)}</div>
        ) : (
          <p className="rounded-2xl bg-[#FFF3C4] p-6 text-sm" data-testid="community-featured-empty">
            {ads === null ? "Loading…" : "No B Marketplace ads yet. Got something you no longer need? Post a free ad and it could appear right here."}
          </p>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-[#202A35]/60" data-testid="community-featured-note">{SELLER_NOTE}</p>
          <Link to="/community/sell" className="text-xs font-bold underline" data-testid="community-featured-how-to-sell">How selling works →</Link>
        </div>
      </div>
    </section>
  );
};
